/**
 * Build a wa.me link from a raw phone number.
 * WhatsApp requires an international dial-code with no `+` or spaces.
 * Heuristic: UK-first (matches this site's audience), fall back to whatever
 * digits the number already has. Returns null if we can't produce anything
 * WhatsApp will accept.
 *
 * `message` (optional) is a pre-filled first message for the chat — appended
 * as `?text=` per the WhatsApp Universal Link spec. Keep it short; long
 * messages hit URL length limits on some Android clients.
 */
export function toWhatsAppUrl(tel, message) {
  if (!tel) return null;
  let digits = String(tel).replace(/[^\d+]/g, "");
  if (digits.startsWith("+")) digits = digits.slice(1);
  // UK numbers pasted as "01992 812 700" — strip the leading 0 and prefix 44.
  if (digits.startsWith("0") && digits.length >= 10 && digits.length <= 11) {
    digits = "44" + digits.slice(1);
  }
  if (digits.length < 8) return null; // too short to be a real international number
  const base = `https://wa.me/${digits}`;
  return message ? `${base}?text=${encodeURIComponent(message)}` : base;
}

/** Same pre-filled inquiry message used everywhere a WhatsApp CTA links
 * straight to the listing agent, so the chip on the property page and the
 * WhatsApp option in the Share modal always say the same thing. */
export function buildWhatsAppInquiryMessage(property, pageUrl) {
  if (!property) return "";
  const bits = [];
  const title = property.title || property.display_address;
  if (title) bits.push(`Hi, I'm interested in "${title}"`);
  else bits.push("Hi, I'm interested in one of your properties");
  if (property.formatted_price || property.price) {
    const priceStr = property.formatted_price
      || `${property.currency || "£"}${Number(property.price).toLocaleString()}`;
    bits.push(`priced at ${priceStr}`);
  }
  if (property.display_address) bits.push(`(${property.display_address})`);
  let msg = bits.join(" ") + ".";
  if (pageUrl) msg += `\n${pageUrl}`;
  return msg;
}

/** Resolves the agent WhatsApp link for a property, honouring the
 * "Accept WhatsApp communication" toggle — returns null when disabled or
 * when no usable number is set, so callers can hide the CTA entirely. */
export function getAgentWhatsAppHref(property, pageUrl) {
  if (!property || property.agent_whatsapp_enabled === false) return null;
  const tel = property.agent_whatsapp_tel || property.agent_tel;
  return toWhatsAppUrl(tel, buildWhatsAppInquiryMessage(property, pageUrl));
}
